import React from 'react';
import { 
  User, Phone, Mail, Shield, AlertCircle
} from 'lucide-react';

/**
 * CustomerInformationSection Component
 * Handles rendering of customer information fields (name, whatsapp, email, license)
 * NO LICENSE FORMAT VALIDATION - Accepts any text/number format
 * Extracted from SimplifiedReservationForm for better code organization
 */
const CustomerInformationSection = ({
  reservationData,
  handleEnhancedInputChange,
  fieldValidation,
  reservationSettings,
  isSaving,
  uploadingDocuments
}) => {
  
  // Get icon component for field
  const getFieldIcon = (iconName) => {
    const icons = {
      User, 
      Phone, 
      Mail, 
      Shield
    };
    return icons[iconName] || User;
  };

  // Get customer information fields from settings
  const getCustomerFields = () => {
    const fieldSettings = reservationSettings.fieldSettings || {};
    const customerFieldKeys = ['customerName', 'whatsappNumber', 'email', 'licenseNumber'];
    
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => 
        fieldConfig && 
        fieldConfig.visible && 
        customerFieldKeys.includes(fieldKey)
      )
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Render individual customer field - NO LICENSE VALIDATION
  const renderCustomerField = (fieldKey, fieldConfig) => {
    if (!fieldConfig || !fieldConfig.visible) return null;

    const value = reservationData[fieldKey] || '';
    const validation = fieldValidation[fieldKey];
    const hasError = validation && !validation.isValid;
    const IconComponent = getFieldIcon(fieldConfig.icon);

    const commonProps = {
      name: fieldKey,
      value: value,
      onChange: handleEnhancedInputChange,
      disabled: isSaving || uploadingDocuments,
      required: fieldConfig.required,
      className: `w-full p-3 bg-gray-700 border text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 transition-colors ${
        hasError ? 'border-red-500 bg-red-900/20' : 'border-gray-600'
      } ${reservationSettings.formBehavior?.compactMode ? 'p-2 text-sm' : 'p-3'}`,
      placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label.toLowerCase()}`
    };

    const renderFieldInput = () => {
      switch (fieldKey) {
        case 'customerName':
          return (
            <input
              type="text"
              {...commonProps}
              minLength={reservationSettings.validationRules?.customerNameMinLength || 2}
            />
          );

        case 'whatsappNumber':
          return (
            <input
              type="tel"
              {...commonProps}
              pattern={fieldConfig.validationPattern}
            />
          );

        case 'email':
          return (
            <input
              type="email"
              {...commonProps}
            />
          );

        case 'licenseNumber':
          // NO VALIDATION - Accept any format
          return (
            <input
              type="text"
              {...commonProps}
              placeholder="Enter license number (any format accepted)"
            />
          );

        default:
          return (
            <input
              type="text"
              {...commonProps}
            />
          );
      }
    };

    return (
      <div key={fieldKey} className={reservationSettings.formBehavior?.compactMode ? 'space-y-1' : 'space-y-2'}>
        <label className="block text-sm font-medium text-gray-300">
          <div className="flex items-center gap-2">
            {reservationSettings.formBehavior?.showFieldIcons && (
              <IconComponent size={16} className="text-orange-400" />
            )}
            <span>
              {fieldConfig.label} 
              {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
            </span>
            
            {fieldKey === 'email' && reservationSettings.requireCustomerEmail && (
              <span className="text-xs text-blue-400">(Required by settings)</span>
            )}
            {fieldKey === 'licenseNumber' && (
              <span className="text-xs text-green-400">(No Format Validation - Any text accepted)</span>
            )}
          </div>
          
          {reservationSettings.formBehavior?.showFieldDescriptions && fieldConfig.description && (
            <div className="text-xs text-gray-500 mt-1 font-normal">
              {fieldConfig.description}
            </div>
          )}
        </label>
        
        {renderFieldInput()}
        
        {hasError && validation.errors.length > 0 && (
          <div className="space-y-1">
            {validation.errors.map((error, index) => (
              <div key={index} className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle size={12} />
                <span>{error}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const customerFields = getCustomerFields();

  // Don't render section if no fields are visible
  if (customerFields.length === 0) return null;

  return (
    <div className={reservationSettings.formBehavior?.compactMode ? 'space-y-3' : 'space-y-4'}>
      {/* Section Header */}
      {reservationSettings.formBehavior?.groupRelatedFields && (
        <div className="border-b border-gray-700 pb-2">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            Customer Information
          </h4>
          {/* ✅ CHANGED: "4 fields • Contact and identification details" line removed */}
        </div>
      )}
      
      {/* Customer Fields Grid */}
      <div className={`grid gap-${reservationSettings.formBehavior?.compactMode ? '4' : '6'} ${
        reservationSettings.formBehavior?.compactMode 
          ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
          : 'grid-cols-1 md:grid-cols-2'
      }`}>
        {customerFields.map(([fieldKey, fieldConfig]) => renderCustomerField(fieldKey, fieldConfig))}
      </div>

      {/* Cross-field validation hint */}
      {reservationSettings.validationRules?.requirePhoneOrEmail && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
          <div className="flex items-center text-blue-300 text-sm">
            <AlertCircle size={14} className="mr-2" />
            <span>Either WhatsApp number or email is required</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInformationSection;